// ============================================================================
// SliderSelector.jsx — Corelia UI v1.2
// Slider discret avec labels (horizontal/vertical) + FormLayout + thème unifié
// - Orientation: "horizontal" | "vertical"
// - Robustesse : clamp value, gestion null/undefined, marks auto cohérents
// - UX : labels longs gérés, disabled, valeur courante optionnelle
// ============================================================================

import React, { useMemo, useId } from "react";
import theme from "../../../styles/theme";
import FormLayout from "./FormLayout";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const roundToStep = (value, min, step) => {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(step) || step <= 0) return value;
  const n = Math.round((value - min) / step);
  const rounded = min + n * step;
  const decimals = String(step).includes(".") ? String(step).split(".")[1].length : 0;
  return Number(rounded.toFixed(decimals));
};

const buildAutoMarks = (min, max, step) => {
  const safeStep = Number.isFinite(step) && step > 0 ? step : 1;
  const span = max - min;
  const count = Math.floor(span / safeStep) + 1;

  if (!Number.isFinite(count) || count <= 0 || count > 9) {
    const mid = roundToStep(min + span / 2, min, safeStep);
    const uniq = Array.from(new Set([min, mid, max]));
    return uniq.map((v) => ({ value: v, label: String(v) }));
  }

  const marks = [];
  for (let i = 0; i < count; i += 1) {
    const v = roundToStep(min + i * safeStep, min, safeStep);
    marks.push({ value: v, label: String(v) });
  }
  return marks;
};

const normalizeMarks = (marks, min, max, step) => {
  if (!Array.isArray(marks) || marks.length === 0) return buildAutoMarks(min, max, step);

  const cleaned = marks
    .map((m) => ({ value: Number(m?.value), label: m?.label != null ? String(m.label) : String(m?.value ?? "") }))
    .filter((m) => Number.isFinite(m.value))
    .map((m) => ({ ...m, value: clamp(roundToStep(m.value, min, step), min, max) }))
    .sort((a, b) => a.value - b.value);

  const out = [];
  const seen = new Set();
  cleaned.forEach((m) => {
    const k = String(m.value);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(m);
    }
  });

  return out.length ? out : buildAutoMarks(min, max, step);
};

const StepSelector = ({
  label,
  labelPosition = "left",
  labelWidth = "130px",
  align = "center",

  value,
  onChange,

  min = 1,
  max = 5,
  step = 1,

  marks = null, // [{ value, label }]
  disabled = false,
  width = "100%",
  style = {},

  showValue = false,
  valueFormatter = (v) => String(v),

  orientation = "horizontal", // "horizontal" | "vertical"
  sliderLength = 140,         // px, utilisé seulement si vertical
}) => {
  const reactId = useId();
  const inputId = `corelia-slider-${reactId}`;

  const isVertical = orientation === "vertical";

  const safeMin = Number.isFinite(Number(min)) ? Number(min) : 1;
  const safeMax = Number.isFinite(Number(max)) ? Number(max) : 5;
  const safeStep = Number.isFinite(Number(step)) && Number(step) > 0 ? Number(step) : 1;

  const minV = Math.min(safeMin, safeMax);
  const maxV = Math.max(safeMin, safeMax);

  const normalizedMarks = useMemo(
    () => normalizeMarks(marks, minV, maxV, safeStep),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [marks, minV, maxV, safeStep]
  );

  const hasValue = value !== null && value !== undefined && value !== "";

  const currentValue = useMemo(() => {
    if (!hasValue) return minV;
    const num = Number(value);
    if (!Number.isFinite(num)) return minV;
    return clamp(roundToStep(num, minV, safeStep), minV, maxV);
  }, [hasValue, value, minV, maxV, safeStep]);

  const handleChange = (e) => {
    if (!onChange) return;
    const raw = Number(e.target.value);
    if (!Number.isFinite(raw)) {
      onChange(null);
      return;
    }
    const v = clamp(roundToStep(raw, minV, safeStep), minV, maxV);
    onChange(v);
  };

  const sliderStyle = isVertical
    ? {
        width: sliderLength,
        height: 6,
        transform: "rotate(-90deg)",
        transformOrigin: "center",
      }
    : { width: "100%" };

  return (
    <FormLayout
      label={label}
      labelPosition={labelPosition}
      labelWidth={labelWidth}
      align={align}
      width={width}
      style={style}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: isVertical ? "row" : "column",
          gap: isVertical ? 14 : 8,
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        {/* Slider + valeur */}
        <div
          style={{
            display: "flex",
            flexDirection: isVertical ? "column" : "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
          }}
        >
          <input
            id={inputId}
            type="range"
            min={minV}
            max={maxV}
            step={safeStep}
            value={currentValue}
            onChange={handleChange}
            disabled={disabled}
            aria-label={label || "Slider"}
            style={{
              margin: 0,
              accentColor: theme.colors.text.corelia,
              ...sliderStyle,
            }}
          />

          {showValue && (
            <div
              style={{
                minWidth: 42,
                textAlign: isVertical ? "center" : "right",
                fontFamily: theme.form.label.fontFamily,
                fontSize: theme.fontSizes.xs,
                color: theme.colors.text.small,
                userSelect: "none",
              }}
              title={valueFormatter(currentValue)}
            >
              {valueFormatter(currentValue)}
            </div>
          )}
        </div>

        {/* Labels */}
        <div
          style={{
            display: "flex",
            flexDirection: isVertical ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isVertical ? "flex-start" : "flex-start",
            gap: 6,
            width: "100%",
          }}
        >
          {normalizedMarks.map((m) => {
            const isActive = m.value === currentValue;
            return (
              <div
                key={m.value}
                title={m.label}
                style={{
                  textAlign: isVertical ? "left" : "center",
                  flex: isVertical ? "unset" : 1,
                  fontFamily: theme.form.label.fontFamily,
                  fontSize: theme.fontSizes.xs,
                  color: isActive ? theme.colors.text.corelia : theme.colors.text.small,
                  lineHeight: 1.2,
                  userSelect: "none",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                <div
                  style={{
                    fontWeight: isActive ? theme.fontWeights.medium : theme.fontWeights.normal,
                    maxWidth: "100%",
                  }}
                >
                  {m.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </FormLayout>
  );
};

export default StepSelector;